import React, { useCallback, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useDropzone } from 'react-dropzone';
import analytics from '../services/analytics';

const FileUpload = forwardRef(({ onFilesSelected, onClearAll, shouldClear }, ref) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Industry standard: Expose imperative methods to parent
  useImperativeHandle(ref, () => ({
    clearFiles: () => {
      setUploadedFiles([]);
    },
    getFileCount: () => uploadedFiles.length,
    validateState: () => {
      // Industry standard: State validation
      return {
        isValid: true,
        fileCount: uploadedFiles.length,
        hasFiles: uploadedFiles.length > 0
      };
    }
  }), [uploadedFiles]);

  // Industry standard: Listen for external clear signals
  useEffect(() => {
    if (shouldClear) {
      setUploadedFiles([]);
    }
  }, [shouldClear]);

  const onDrop = useCallback((acceptedFiles) => {
    const filesWithId = acceptedFiles.map(file => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      status: 'ready',
      selected: true // Default to selected for new uploads
    }));
    
    // Track file upload analytics
    acceptedFiles.forEach(file => {
      analytics.trackFileUpload(file.type, file.size);
    });
    
    setUploadedFiles(prev => {
      const newFiles = [...prev, ...filesWithId];
      onFilesSelected(newFiles); // Send complete updated list
      return newFiles;
    });
  }, [onFilesSelected]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff', '.webp'],
      'text/plain': ['.txt'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-powerpoint': ['.ppt'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
      'text/html': ['.html'],
      'text/markdown': ['.md']
    },
    multiple: true,
    maxSize: 10 * 1024 * 1024 // 10MB
  });

  const removeFile = (fileId) => {
    setUploadedFiles(prev => {
      const newFiles = prev.filter(file => file.id !== fileId);
      onFilesSelected(newFiles); // Keep parent in sync
      return newFiles;
    });
  };

  // Industry standard: Drag and drop reordering
  const handleDragStart = (e, index) => {
    setDraggedItem({ index, file: uploadedFiles[index] });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = (e) => {
    // Only clear if we're leaving the container, not moving between items
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    setDragOverIndex(null);
    
    if (draggedItem && draggedItem.index !== dropIndex) {
      const newFiles = [...uploadedFiles];
      const draggedFile = newFiles[draggedItem.index];
      
      // Remove dragged item
      newFiles.splice(draggedItem.index, 1);
      
      // Insert at new position
      const insertIndex = draggedItem.index < dropIndex ? dropIndex - 1 : dropIndex;
      newFiles.splice(insertIndex, 0, draggedFile);
      
      setUploadedFiles(newFiles);
      onFilesSelected(newFiles);
    }
    
    setDraggedItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  // Move file up/down (keyboard/button alternative)
  const moveFile = (index, direction) => {
    const newFiles = [...uploadedFiles];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex >= 0 && targetIndex < newFiles.length) {
      [newFiles[index], newFiles[targetIndex]] = [newFiles[targetIndex], newFiles[index]];
      setUploadedFiles(newFiles);
      onFilesSelected(newFiles);
    }
  };

  // Handle individual file selection
  const toggleFileSelection = (fileId) => {
    setUploadedFiles(prev => {
      const newFiles = prev.map(file => 
        file.id === fileId ? { ...file, selected: !file.selected } : file
      );
      onFilesSelected(newFiles);
      return newFiles;
    });
  };

  // Select all/none functionality
  const toggleSelectAll = () => {
    const allSelected = uploadedFiles.every(file => file.selected);
    setUploadedFiles(prev => {
      const newFiles = prev.map(file => ({ ...file, selected: !allSelected }));
      onFilesSelected(newFiles);
      return newFiles;
    });
  };

  // Get selection statistics
  const selectedCount = uploadedFiles.filter(file => file.selected).length;
  const allSelected = uploadedFiles.length > 0 && selectedCount === uploadedFiles.length;
  const someSelected = selectedCount > 0 && selectedCount < uploadedFiles.length;

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const clearAll = () => {
    // Confirm destructive action (industry standard)
    const hasConversions = uploadedFiles.length > 0;
    
    if (hasConversions) {
      const confirmed = window.confirm(
        '⚠️ This will remove all uploaded files and start over.\n\n' +
        'Note: Use "Clear Results" in the results section if you only want to clear conversion outputs.\n\n' +
        'Are you sure you want to remove all files?'
      );
      
      if (!confirmed) {
        return; // User cancelled
      }
    }

    // Clear local file list and notify parent immediately
    setUploadedFiles([]);
    onFilesSelected([]); // Keep parent in sync
    
    // Notify parent to perform full application reset
    if (onClearAll) {
      onClearAll();
    }
  };

  return (
    <div className="file-upload">
      <div 
        {...getRootProps()} 
        className={`dropzone ${isDragActive ? 'active' : ''}`}
        style={{
          border: '2px dashed #ccc',
          borderRadius: '10px',
          padding: '40px',
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: isDragActive ? '#f0f8ff' : '#fafafa',
          borderColor: isDragActive ? '#007bff' : '#ccc',
          transition: 'all 0.3s ease'
        }}
      >
        <input {...getInputProps()} />
        <div style={{ fontSize: '48px', marginBottom: '20px', color: '#007bff' }}>
          📁
        </div>
        {isDragActive ? (
          <p style={{ fontSize: '18px', color: '#007bff' }}>Drop the files here...</p>
        ) : (
          <div>
            <p style={{ fontSize: '18px', marginBottom: '10px' }}>
              Drag & drop files here, or click to select files
            </p>
            <p style={{ fontSize: '14px', color: '#666' }}>
              Supports: Images (JPG, PNG, GIF, etc.), Documents (DOC, DOCX, XLS, XLSX, PPT, PPTX), Text files, HTML, Markdown
            </p>
            <p style={{ fontSize: '12px', color: '#999', marginTop: '10px' }}>
              Maximum file size: 10MB per file
            </p>
          </div>
        )}
      </div>

      {uploadedFiles.length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <h3 style={{ margin: 0 }}>Uploaded Files ({uploadedFiles.length})</h3>
                {uploadedFiles.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#666'
                    }}>
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={checkboxRef => {
                          if (checkboxRef) {
                            checkboxRef.indeterminate = someSelected;
                          }
                        }}
                        onChange={toggleSelectAll}
                        style={{ marginRight: '6px', transform: 'scale(1.1)' }}
                      />
                      {allSelected ? 'Deselect All' : someSelected ? 'Select All' : 'Select All'}
                    </label>
                    <span style={{ 
                      fontSize: '12px', 
                      color: selectedCount > 0 ? '#28a745' : '#666',
                      fontWeight: selectedCount > 0 ? 'bold' : 'normal'
                    }}>
                      {selectedCount} of {uploadedFiles.length} selected
                    </span>
                  </div>
                )}
              </div>
              
              {uploadedFiles.length > 1 && (
                <p style={{ fontSize: '12px', color: '#666', margin: '0', fontStyle: 'italic' }}>
                  💡 Check files to convert • Drag to reorder • First selected = First page
                </p>
              )}
            </div>
            
            <button 
              className="btn" 
              onClick={clearAll}
              style={{ background: '#dc3545', padding: '8px 16px', fontSize: '14px' }}
              title="Remove all uploaded files and start over"
            >
              🗑️ Clear Files
            </button>
          </div>
          <ul className="file-list" style={{ listStyle: 'none', margin: '20px 0', padding: 0 }}>
            {uploadedFiles.map((fileInfo, index) => (
              <li 
                key={fileInfo.id} 
                className="file-item"
                draggable={uploadedFiles.length > 1}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px',
                  border: `2px solid ${dragOverIndex === index ? '#007bff' : '#e0e0e0'}`,
                  borderRadius: '8px',
                  marginBottom: '8px',
                  backgroundColor: draggedItem?.index === index ? '#f8f9fa' : 
                                   dragOverIndex === index ? '#e7f3ff' : '#fafafa',
                  cursor: uploadedFiles.length > 1 ? 'grab' : 'default',
                  opacity: draggedItem?.index === index ? 0.5 : 1,
                  transition: 'all 0.2s ease',
                  position: 'relative'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    cursor: 'pointer',
                    marginRight: '12px'
                  }}>
                    <input
                      type="checkbox"
                      checked={fileInfo.selected}
                      onChange={() => toggleFileSelection(fileInfo.id)}
                      style={{ 
                        marginRight: '8px', 
                        transform: 'scale(1.2)',
                        cursor: 'pointer'
                      }}
                    />
                  </label>
                  {uploadedFiles.length > 1 && (
                    <div style={{ 
                      marginRight: '12px', 
                      color: '#666',
                      fontSize: '16px',
                      cursor: 'grab',
                      userSelect: 'none'
                    }}>
                      <span style={{ 
                        display: 'inline-block',
                        padding: '4px 8px',
                        backgroundColor: '#e9ecef',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        minWidth: '24px',
                        textAlign: 'center'
                      }}>
                        {index + 1}
                      </span>
                      <span style={{ marginLeft: '6px' }}>⋮⋮</span>
                    </div>
                  )}
                  <div style={{ flex: 1 }}>
                    <strong style={{ color: fileInfo.selected ? '#333' : '#999' }}>{fileInfo.name}</strong>
                    <div style={{ fontSize: '12px', color: fileInfo.selected ? '#666' : '#999' }}>
                      {formatFileSize(fileInfo.size)} • {fileInfo.type || 'Unknown type'}
                    </div>
                  </div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {uploadedFiles.length > 1 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <button
                        className="move-button"
                        onClick={() => moveFile(index, 'up')}
                        disabled={index === 0}
                        style={{
                          background: index === 0 ? '#e9ecef' : '#007bff',
                          color: index === 0 ? '#6c757d' : 'white',
                          border: 'none',
                          borderRadius: '3px',
                          padding: '2px 6px',
                          cursor: index === 0 ? 'not-allowed' : 'pointer',
                          fontSize: '10px',
                          lineHeight: '1'
                        }}
                        title="Move up"
                      >
                        ▲
                      </button>
                      <button
                        className="move-button"
                        onClick={() => moveFile(index, 'down')}
                        disabled={index === uploadedFiles.length - 1}
                        style={{
                          background: index === uploadedFiles.length - 1 ? '#e9ecef' : '#007bff',
                          color: index === uploadedFiles.length - 1 ? '#6c757d' : 'white',
                          border: 'none',
                          borderRadius: '3px',
                          padding: '2px 6px',
                          cursor: index === uploadedFiles.length - 1 ? 'not-allowed' : 'pointer',
                          fontSize: '10px',
                          lineHeight: '1'
                        }}
                        title="Move down"
                      >
                        ▼
                      </button>
                    </div>
                  )}
                  
                  <span 
                    style={{ 
                      padding: '4px 8px', 
                      borderRadius: '4px', 
                      fontSize: '12px',
                      backgroundColor: fileInfo.status === 'ready' ? '#28a745' : '#ffc107',
                      color: 'white'
                    }}
                  >
                    {fileInfo.status}
                  </span>
                  
                  <button
                    onClick={() => removeFile(fileInfo.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#dc3545',
                      cursor: 'pointer',
                      fontSize: '18px',
                      padding: '4px'
                    }}
                    title="Remove file"
                  >
                    ×
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
});

export default FileUpload;